import { createRouter, createWebHistory } from 'vue-router'
import app from '@/app.js'
import multiguard from 'vue-router-multiguard';
import {
  redirectUnauthenticated,
  redirectAuthenticated,
  redirectUnauthorized,
  redirectFromIndex,
} from './traps';

// routes
import auth from './routes/auth';
import siteMessages from './routes/siteMessages';
import billing from './routes/billing';
import businessUsers from './routes/businessUsers';
import businesses from './routes/businesses';
import cannedReplies from './routes/cannedReplies';
import contracts from './routes/contracts';
import creatorProgram from './routes/creatorProgram';
import events from './routes/events';
import grooveWidget from './routes/grooveWidget';
import home from './routes/home';
import internalUsers from './routes/internalUsers';
import labels from './routes/labels';
import networkReview from './routes/networkReview';
import notFound from './routes/notFound';
import packages from './routes/packages';
import payments from './routes/payments';
import pins from './routes/pins';
import promotions from './routes/promotions';
import reports from './routes/reports';
import scheduledThreads from './routes/scheduledThreads';
import sponsorships from './routes/sponsorships';
import threads from './routes/threads';
import tracking from './routes/tracking';
import users from './routes/users';
import terms from './routes/terms';
import verifyLink from './routes/verifyLink';
import { nextTick } from 'vue';
import { loadRecaptcha } from '@/plugins/recaptcha';

const router = createRouter({
  history: createWebHistory('/'),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
  routes: [
    ...auth,
    ...billing,
    ...businesses,
    ...businessUsers,
    ...cannedReplies,
    ...contracts,
    ...creatorProgram,
    ...events,
    ...grooveWidget,
    ...home,
    ...internalUsers,
    ...labels,
    ...networkReview,
    ...packages,
    ...payments,
    ...pins,
    ...promotions,
    ...reports,
    ...scheduledThreads,
    ...siteMessages,
    ...sponsorships,
    ...threads,
    ...terms,
    ...tracking,
    ...users,
    ...verifyLink,
    ...notFound,
  ],
});

if (process.env.NODE_ENV !== 'test') {
  router.app = app;
}

// configure traps
router.beforeEach(
  multiguard([
    redirectUnauthenticated,
    redirectAuthenticated,
    redirectUnauthorized,
    redirectFromIndex,
  ]),
);

// set the title of the page
const DEFAULT_TITLE = 'Pepper Business';
router.afterEach((to, from, failure) => {
    // reCAPTCHA v3 is only used on the guest/auth pages (login/register/forgot/reset).
    // Load it (once) and reveal its badge there; hide the badge everywhere else so it
    // does not linger on logged-in pages.
    //
    // Skip aborted navigations. When a trap bounces a logged-in user off an auth
    // URL, vue-router fires this hook for the redirect target first and for the
    // aborted auth route second, so acting on the latter would leave the badge
    // showing on the page we actually landed on.
    if (!failure) {
        const onAuthPage = to.matched.some((record) => record.meta.isAuthPage);
        if (onAuthPage) {
            // Nothing to do on failure: the forms surface the error when they try
            // to execute, and an unhandled rejection here would be a console error.
            loadRecaptcha().catch((err) => err);
        }
        document.body.classList.toggle('recaptcha-visible', onAuthPage);
    }

    nextTick(() => {
        const viewTitle = to.meta.title;
        document.title = viewTitle ? `${viewTitle} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    });
});

if (!app.config.globalProperties.$router) {
  app.use(router);
}

export default router;
